import Backdrop from '@atoms/Backdrop/Backdrop';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Modal from '@atoms/Modal/Modal';
import Tooltip from '@atoms/Tooltip/Tooltip';
import { easeInOut } from '@config/animations.config';
import { IHeader } from '@models/IHeader';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import TooltipPositionEnum from '@models/enums/TooltipPositionEnum';
import VerticalDirectionEnum from '@models/enums/VerticalDirectionEnum';
import AccountDropdown from '@molecules/AccountDropdown/AccountDropdown';
import Dropdown from '@molecules/Dropdown/Dropdown';
import MiniBasket from '@molecules/MiniBasket/MiniBasket';
import Search from '@organisms/Search/Search';
import useCartSizeStore from '@state/CartSizeStore';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import bodyScrollLock from '@utils/helpers/bodyScrollLock';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useCookieConsent from '@utils/hooks/useCookieConsent';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import DesktopSubNav from './subNav/desktop/DesktopSubNav';
import MobileSubNav from './subNav/mobile/MobileSubNav';

const Header = ({
  accountDropdownNavigation,
  accountLabel,
  accountLink = {},
  accountsEnabled,
  backLabel,
  basketLabel,
  basketLink,
  cartIdCookie,
  cartSizeCookie,
  cookieDomain,
  culture,
  hasMiniBasket,
  endpoint,
  homepageLink,
  language,
  languageSelector,
  listingPage,
  logo,
  mainNavigation,
  miniBasketEndpoint,
  miniBasketTitle,
  nikonImagingText,
  overlaySearchIcon,
  popularSearches,
  searchBlock,
  searchIcon,
  searchLabel,
  searchPlaceholder,
  searchSubTitle,
  serviceAccountLink,
  showAccountIcon,
  showSearchIcon,
  skipContentLabel,
  suggestedSearchTitleLabel,
}: IHeader) => {
  const [isOpenDesktop, setIsOpenDesktop] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isMobileActive, setIsMobileActive] = useState(false);
  const [isMiniBasketDisplayed, setIsMiniBasketDisplayed] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchButtonHovered, setIsSearchButtonHovered] = useState(false);
  const [isLanguageDropdownSelectorOpen, setIsLanguageDropdownSelectorOpen] =
    useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [languageActiveIndex, setLanguageActiveIndex] = useState(-1);
  const [isLanguageMobileModalOpen, setIsLanguageMobileModalOpen] =
    useState(false);
  const [mainSelector, setMainSelector] = useState<HTMLElement | null>(null);
  const isInIFrame = useIsInIFrame();
  const isTouchDevice = useIsTouchDevice();
  const isMobile = useMediaQuery(768);
  const isMobileView = useMediaQuery(1440);
  const { url: homePageUrl, target: homePageTarget = '_self' } =
    homepageLink || {};
  const [navRef, { height: navHeight }] = useResizeObserver();
  const { siteNotificationsHeight, USPsStripHeight } =
    useComponentsHeightsStore((state) => ({
      siteNotificationsHeight: state.siteNotificationsHeight,
      USPsStripHeight: state.USPsStripHeight,
    }));

  useCookieConsent();

  const handleLevelOneClick = (item: string) => {
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'header_menu_level_1',
      main_category: item,
      sub_category: undefined,
      tertiary_category: undefined,
    });
  };

  const handleSearchButtonClick = () => {
    setActiveIndex(-1);
    setIsOpenDesktop(false);
    setIsLanguageDropdownSelectorOpen(false);
    setIsAccountDropdownOpen(false);
    setIsSearchOpen((oldState) => !oldState);
    bodyScrollLock(!isSearchOpen);
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'quick_menu',
      main_category: 'search',
    });
  };

  const {
    cartSize,
    setCartIdName,
    setCartSizeName,
    setDomainName,
    cartSizeName,
    setCartSize,
  } = useCartSizeStore((state) => ({
    cartSize: state.cartSize,
    setCartIdName: state.setCartIdName,
    setCartSizeName: state.setCartSizeName,
    setDomainName: state.setDomainName,
    setCartSize: state.setCartSize,
    cartIdName: state.cartIdName,
    cartSizeName: state.cartSizeName,
    domainName: state.domainName,
  }));

  useEffect(() => {
    setCartIdName(cartIdCookie);
    setCartSizeName(cartSizeCookie);
    setDomainName(cookieDomain);
  }, [
    cartIdCookie,
    cartSizeCookie,
    cookieDomain,
    setCartIdName,
    setCartSizeName,
    setDomainName,
  ]);

  useEffect(() => {
    if (cartSizeName) {
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);
    }
  }, [cartSizeName, setCartSize]);

  const {
    mainNavigationAriaLabel,
    openMenuAriaLabel,
    backToFirstLevelNavigationAriaLabel,
    closeMobileNavigationAriaLabel,
    overlayToCloseDesktopNavigationAriaLabel,
  } = useAriaLabels();

  useEffect(
    () =>
      setLanguageActiveIndex(
        languageSelector?.links?.findIndex(
          (lang) => !!lang.url?.includes(language)
        ) ?? -1
      ),
    [language, languageSelector]
  );

  useEffect(() => {
    if (
      isMobileActive ||
      isSearchOpen ||
      isLanguageDropdownSelectorOpen ||
      isAccountDropdownOpen
    ) {
      bodyScrollLock(true);
    } else {
      bodyScrollLock(false);
    }

    return () => {
      bodyScrollLock(false);
    };
  }, [
    isMobileActive,
    isSearchOpen,
    isLanguageDropdownSelectorOpen,
    isAccountDropdownOpen,
  ]);

  useEffect(() => {
    setMainSelector(document.querySelector('main'));
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpenDesktop(false);
        setIsLanguageDropdownSelectorOpen(false);
        setIsAccountDropdownOpen(false);
        setIsSearchOpen(false);
        setIsMiniBasketDisplayed(false);
      }
    };

    if (
      isOpenDesktop ||
      isLanguageDropdownSelectorOpen ||
      isAccountDropdownOpen ||
      isSearchOpen ||
      isMiniBasketDisplayed
    ) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isOpenDesktop,
    isLanguageDropdownSelectorOpen,
    isAccountDropdownOpen,
    isSearchOpen,
    isMiniBasketDisplayed,
  ]);

  // ONLY RELEVANT WHEN ACCOUNTS ARE DISABLED
  const {
    url: accountPageUrl,
    target: accountPageTarget = '_self',
    name: accountName,
  } = accountLink;

  const showLanguageSelector = !!languageSelector?.links?.length && language;
  const showIcons =
    isMobileView ||
    showLanguageSelector ||
    showAccountIcon ||
    showSearchIcon ||
    (basketLink?.url && basketLink?.name);
  const showNav =
    logo || (!isMobileView && !!mainNavigation?.length) || showIcons;

  return (
    <>
      {mainSelector && (
        <div className="sr-container">
          <button
            onClick={() => mainSelector.focus()}
            className="relative left-7.5 z-10 mt-5 h-[50px] rounded-[50px] border-2 border-dotted border-white bg-grey-600 px-[15px] py-2.5 text-white outline-none"
          >
            {skipContentLabel}
          </button>
        </div>
      )}

      {showNav && (
        <nav
          ref={navRef}
          aria-label={mainNavigationAriaLabel}
          className="relative flex flex-col"
          data-testid="CT-01-Header"
        >
          <div className="relative z-80 flex h-[77px] flex-row items-stretch border-b border-grey-500/30 bg-black-100 px-5 py-2.5 lg:h-[129px] lg:px-7 lg:py-3.5">
            {logo && homePageUrl && (
              <a
                href={homePageUrl}
                target={isInIFrame ? '_parent' : homePageTarget}
                rel="noreferrer"
                className="my-2 mr-6 lg:my-4"
                data-testid="CT-01-Header-Navigation-Logo-Link"
              >
                <Image
                  sizes="" // TODO need to set the width for the logo so show correctly as does not have a width until image is loaded
                  lazyLoad={false}
                  image={logo}
                  imageClasses="h-10 w-10 lg:h-[4.25rem] lg:w-[4.25rem]"
                  progressiveLoad={false}
                  dataTestId="CT-01-Header-Navigation-Logo-Image"
                />
              </a>
            )}
            {logo && !homePageUrl && (
              <Image
                sizes="" // TODO need to set the width for the logo so show correctly as does not have a width until image is loaded
                lazyLoad={false}
                image={logo}
                imageClasses="h-10 w-10 lg:h-[4.25rem] lg:w-[4.25rem] my-2 mr-6 lg:my-4"
                progressiveLoad={false}
                dataTestId="CT-01-Header-Navigation-Logo-Image"
              />
            )}

            {!isMobileView && !!mainNavigation?.length && (
              <ul className="hidden items-stretch justify-center xl:flex">
                {mainNavigation.map(({ link, title, isHighlighted }, index) => {
                  const { url, target } = link || {};

                  return (
                    <li
                      key={`level-1-nav-link--${title}`}
                      className="body-two flex cursor-pointer items-center pr-6 text-white transition-colors hover:text-yellow"
                    >
                      <a
                        className={classNames(
                          'flex items-center self-stretch',
                          {
                            'text-yellow': isHighlighted,
                          }
                        )}
                        href={url || '#'}
                        rel="noreferrer"
                        target={isInIFrame ? '_parent' : target}
                        data-testid={`CT-01-Header-Item-${title}`}
                        onClick={() => handleLevelOneClick(title || '')}
                        onMouseOver={() => {
                          setActiveIndex(index);
                          setIsOpenDesktop(true);
                          setIsSearchOpen(false);
                          setIsLanguageDropdownSelectorOpen(false);
                          setIsAccountDropdownOpen(false);
                        }}
                        onFocus={() => {
                          setActiveIndex(index);
                          setIsOpenDesktop(true);
                          setIsSearchOpen(false);
                          setIsLanguageDropdownSelectorOpen(false);
                          setIsAccountDropdownOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (index === activeIndex) {
                              setActiveIndex(-1);
                              setIsOpenDesktop(false);
                            } else {
                              setActiveIndex(index);
                              setIsOpenDesktop(true);
                              setIsSearchOpen(false);
                              setIsLanguageDropdownSelectorOpen(false);
                              setIsAccountDropdownOpen(false);
                            }
                          }
                        }}
                      >
                        {title}
                      </a>
                      {mainNavigation.length > 0 &&
                        activeIndex === index &&
                        activeIndex >= 0 &&
                        !mainNavigation[activeIndex]?.link?.url && (
                          <AnimatePresence initial={false}>
                            {isOpenDesktop &&
                              mainNavigation.length > 0 &&
                              activeIndex >= 0 && (
                                <DesktopSubNav
                                  isOpenDesktop={isOpenDesktop}
                                  setIsOpenDesktop={setIsOpenDesktop}
                                  activeIndex={activeIndex}
                                  mainNavigation={mainNavigation}
                                />
                              )}
                          </AnimatePresence>
                        )}
                    </li>
                  );
                })}
              </ul>
            )}

            {(nikonImagingText || showIcons) && (
              <div className="ml-auto flex items-center justify-center gap-x-[15px] md:gap-x-5 lg:gap-x-[25px]">
                {nikonImagingText && (
                  <div className="small-text text-grey">{nikonImagingText}</div>
                )}
                {nikonImagingText && showIcons && (
                  <div className="small-text text-white">|</div>
                )}
                {showIcons && (
                  <>
                    {!isMobileView && (
                      <>
                        {showLanguageSelector && (
                          <div className="hidden cursor-pointer items-center justify-center xl:flex">
                            <Dropdown
                              openFromDirection={VerticalDirectionEnum.Down}
                              links={languageSelector.links}
                              modalTitle={languageSelector.title}
                              title={
                                languageSelector.links[languageActiveIndex]
                                  ?.shortName
                              }
                              isOpen={isLanguageDropdownSelectorOpen}
                              setIsOpen={setIsLanguageDropdownSelectorOpen}
                              activeIndex={languageActiveIndex}
                              animateUnderline
                              titleCallback={() => {
                                setIsLanguageDropdownSelectorOpen(
                                  !isLanguageDropdownSelectorOpen
                                );
                                setIsOpenDesktop(false);
                                setIsSearchOpen(false);
                                setIsAccountDropdownOpen(false);
                              }}
                              dataTestId="CT-01-Header-Language-Dropdown"
                              dataLayerCallback={(label) => {
                                updateDataLayer({
                                  event: 'menuUsed',
                                  content_type: 'quick_menu',
                                  main_category: 'language',
                                  sub_category: label,
                                });
                              }}
                            />
                          </div>
                        )}
                        {showAccountIcon && (
                          <div className="tooltip-parent relative w-auto text-black-100">
                            {accountsEnabled ? (
                              <AccountDropdown
                                accountLabel={accountLabel}
                                callback={() => {
                                  setIsOpenDesktop(false);
                                  setIsSearchOpen(false);
                                  setIsLanguageDropdownSelectorOpen(false);
                                  updateDataLayer({
                                    event: 'menuUsed',
                                    content_type: 'quick_menu',
                                    main_category: 'account',
                                  });
                                }}
                                isOpen={isAccountDropdownOpen}
                                setIsOpen={setIsAccountDropdownOpen}
                                {...accountDropdownNavigation}
                              />
                            ) : (
                              <a
                                href={accountPageUrl}
                                target={
                                  isInIFrame ? '_parent' : accountPageTarget
                                }
                                className="appearance-none"
                                aria-label={accountLabel}
                                onClick={() => {
                                  updateDataLayer({
                                    event: 'menuUsed',
                                    content_type: 'quick_menu',
                                    main_category: 'account',
                                  });
                                }}
                              >
                                <span className="sr-only">{accountName}</span>
                                <Icon
                                  name="account"
                                  colorClassName="text-white"
                                  size={IconSizeEnum.xl}
                                />
                              </a>
                            )}
                            {accountLabel && (
                              <Tooltip
                                label={accountLabel}
                                position={TooltipPositionEnum.Bottom}
                                hasArrow
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {showSearchIcon && (
                      <>
                        <button
                          className={nikonImagingText ? 'md:hidden' : 'hidden'}
                          type="button"
                          onClick={handleSearchButtonClick}
                          aria-label={searchLabel}
                          data-testid="CT-01-Header-Search-Icon"
                        >
                          <Icon
                            name={searchIcon}
                            className="transition-colors hover:text-yellow"
                            colorClassName="text-white"
                            size={IconSizeEnum.xl}
                          />
                        </button>

                        <motion.button
                          type="button"
                          initial="initial"
                          animate={isSearchButtonHovered ? 'hover' : 'initial'}
                          transition={easeInOut}
                          variants={{
                            hover: {
                              borderRadius: 20,
                            },
                            initial: {
                              borderRadius: 4,
                            },
                          }}
                          className={classNames(
                            'flex border py-1.5 pl-1.5 pr-4 max-xl:min-w-[136px] xl:pr-7.5 xl:transition-colors',
                            isSearchButtonHovered
                              ? 'border-grey-400 bg-grey-450'
                              : 'border-transparent bg-grey-600',
                            {
                              'max-md:hidden': nikonImagingText,
                            }
                          )}
                          onMouseEnter={() => {
                            if (isTouchDevice) return;
                            setIsSearchButtonHovered(true);
                          }}
                          onMouseLeave={() => {
                            if (isTouchDevice) return;
                            setIsSearchButtonHovered(false);
                          }}
                          onClick={handleSearchButtonClick}
                          aria-label={searchLabel}
                          data-testid="CT-01-Header-Search-Button"
                        >
                          <motion.div
                            initial="initial"
                            transition={easeInOut}
                            animate={
                              isSearchButtonHovered ? 'hover' : 'initial'
                            }
                            variants={{
                              hover: {
                                x: 10,
                              },
                              initial: { x: 0 },
                            }}
                            className="flex items-center gap-x-1.5"
                          >
                            <Icon
                              name={searchIcon}
                              colorClassName="text-white"
                              size={IconSizeEnum.xl}
                              ariaHidden
                            />

                            <span className="body-three text-white">
                              {searchLabel}
                            </span>
                          </motion.div>
                        </motion.button>
                      </>
                    )}

                    {((basketLink?.url && basketLink?.name) ||
                      hasMiniBasket) && (
                      <div
                        className={classNames(
                          {
                            'tooltip-parent': !isMobileView && !hasMiniBasket,
                            'z-100': hasMiniBasket,
                          },
                          'relative'
                        )}
                        {...(hasMiniBasket && !isMobileView
                          ? {
                              onMouseEnter: () => {
                                setIsMiniBasketDisplayed(true);
                              },
                              onMouseLeave: () => {
                                setIsMiniBasketDisplayed(false);
                              },
                              onFocus: () => {
                                setIsMiniBasketDisplayed(true);
                              },
                              onBlur: (event) => {
                                if (
                                  !event.currentTarget.contains(
                                    event.relatedTarget
                                  )
                                ) {
                                  setIsMiniBasketDisplayed(false);
                                }
                              },
                            }
                          : {})}
                      >
                        <a
                          href={basketLink.url}
                          className="block cursor-pointer"
                          aria-label={basketLink.name}
                          target={isInIFrame ? '_parent' : '_self'}
                          data-testid="CT-01-Header-Basket"
                          onClick={() => {
                            updateDataLayer({
                              event: 'menuUsed',
                              content_type: 'quick_menu',
                              main_category: 'cart',
                            });
                          }}
                        >
                          <span className="relative block">
                            <span className="sr-only">{basketLink.name}</span>

                            <Icon
                              name="shopping_cart"
                              className="transition-colors hover:text-yellow"
                              colorClassName="text-white"
                              size={IconSizeEnum.xl}
                            />

                            {!!cartSize && (
                              <span className="text-[10px] leading-[16px]">
                                <strong className="absolute -right-[5px] -top-[5px] rounded-full bg-yellow px-[5px] font-bold text-black-100">
                                  {cartSize}
                                </strong>
                              </span>
                            )}
                          </span>
                        </a>

                        {basketLabel && !hasMiniBasket && (
                          <Tooltip
                            label={basketLabel}
                            position={TooltipPositionEnum.BottomLeft}
                            hasArrow
                            backgroundColor="bg-white"
                          />
                        )}

                        <AnimatePresence>
                          {isMiniBasketDisplayed && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              {/* The two divs below are used to increase the hoverable content area around the basket icon
                              so that the mini basket tooltip is still displayed while the user moves the cursor from the icon
                              to the mini basket container */}
                              <div className="absolute -left-6 top-0 h-7 w-6" />
                              <div className="absolute left-1/2 h-10 w-[200px] translate-x-[calc(-100%+26px)]" />

                              <Tooltip
                                position={TooltipPositionEnum.Custom}
                                customContentClassName="left-1/2 translate-x-[calc(-100%+26px)] top-10 rounded-[10px]"
                                customArrowClassName="top-[33px]"
                                arrowRadiusClassName="rounded-[3px]"
                                hasArrow
                                backgroundColor="bg-grey-100"
                                hasTooltipClass={false}
                              >
                                <MiniBasket
                                  miniBasketEndpoint={miniBasketEndpoint}
                                  miniBasketTitle={miniBasketTitle}
                                />
                              </Tooltip>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {isMobileView && (
                      <button
                        className="right-2 top-2 ml-auto flex items-center"
                        onClick={() =>
                          setIsMobileActive((oldState) => !oldState)
                        }
                        onKeyDown={() =>
                          setIsMobileActive((oldState) => !oldState)
                        }
                        aria-label={openMenuAriaLabel}
                      >
                        <Icon
                          name="menu"
                          colorClassName="text-white"
                          size={IconSizeEnum.xl}
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </nav>
      )}
      <AnimatePresence mode="wait">
        {isMobileView && isMobileActive && (
          <MobileSubNav
            accountLink={accountLink}
            accountsEnabled={accountsEnabled}
            accountDropdownNavigation={accountDropdownNavigation}
            language={language}
            languageActiveIndex={languageActiveIndex}
            languageSelector={languageSelector}
            logo={logo}
            mainNavigation={mainNavigation}
            serviceAccountLink={serviceAccountLink}
            setIsOpen={setIsMobileActive}
            showAccountIcon={showAccountIcon}
            backLabel={backLabel}
          />
        )}
      </AnimatePresence>

      <Modal
        isOpen={isLanguageMobileModalOpen}
        setIsOpen={setIsLanguageMobileModalOpen}
        modalClasses="flex flex-col justify-start w-full h-full fixed top-0 bottom-0 left-0 right-0 pt-0 text-black-100 bg-white max-w-none"
        crossClasses="text-black-100"
        contentClasses="py-4"
        showCloseButton={false}
      >
        <div className="container h-full w-full list-none text-black-100">
          <div>
            <div className="flex flex-row items-center pb-11 pt-8 md:pl-8 md:pr-8 lgm:pl-32">
              <button
                className="flex flex-row"
                onClick={() => setIsLanguageMobileModalOpen(false)}
                onKeyDown={() => setIsLanguageMobileModalOpen(false)}
                aria-label={backToFirstLevelNavigationAriaLabel}
              >
                <div className="mr-4 flex h-7 w-7 rotate-180 items-center justify-center rounded-full bg-yellow">
                  <Icon
                    name="east"
                    colorClassName="text-black-100"
                    size={IconSizeEnum.md}
                  />
                </div>
                <p className="flex items-center">{backLabel}</p>
              </button>
              <button
                className="ml-auto flex justify-end"
                onClick={() =>
                  setIsLanguageMobileModalOpen((oldState) => !oldState)
                }
                onKeyDown={() =>
                  setIsLanguageMobileModalOpen((oldState) => !oldState)
                }
                aria-label={closeMobileNavigationAriaLabel}
              >
                <Icon
                  name="close"
                  colorClassName="text-black-100"
                  size="1.125rem"
                />
              </button>
            </div>
          </div>
          <p className="h5 pb-4">{languageSelector?.title}</p>
          {!!languageSelector?.links?.length && (
            <ul>
              {languageSelector.links.map((link, index) => {
                const { longName, url, target } = link;

                return (
                  <li className="py-4" key={`dropdown-item-${longName}`}>
                    <a
                      href={url}
                      target={isInIFrame ? '_parent' : target}
                      className={classNames(
                        'whitespace-nowrap decoration-yellow hover:underline',
                        {
                          'underline decoration-yellow':
                            languageActiveIndex === index,
                        }
                      )}
                    >
                      {longName}
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal>

      <div
        className={classNames(
          'bottom-0 left-0 right-0 top-0 z-[78] bg-transparent',
          isOpenDesktop ? 'fixed' : 'hidden'
        )}
        onClick={() => setIsOpenDesktop(false)}
        onKeyDown={() => setIsOpenDesktop(false)}
        role="button"
        tabIndex={0}
        aria-label={overlayToCloseDesktopNavigationAriaLabel}
      />

      <AnimatePresence>
        {isSearchOpen && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-[79] overflow-y-auto"
            style={{
              top: siteNotificationsHeight + USPsStripHeight + navHeight,
            }}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Search
              popularSearches={popularSearches}
              listingPage={listingPage}
              searchSubTitle={searchSubTitle}
              searchPlaceholder={searchPlaceholder}
              overlaySearchIcon={overlaySearchIcon}
              culture={culture}
              endpoint={endpoint}
              suggestedSearchTitleLabel={suggestedSearchTitleLabel}
              searchBlock={searchBlock}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {(isSearchOpen ||
        isOpenDesktop ||
        isLanguageDropdownSelectorOpen ||
        isAccountDropdownOpen) && (
        <Backdrop
          key="backdrop"
          onCloseCallback={() => {
            if (isSearchOpen) {
              setIsSearchOpen(false);
            }
            if (isOpenDesktop) {
              setIsOpenDesktop(false);
            }
            if (isLanguageDropdownSelectorOpen) {
              setIsLanguageDropdownSelectorOpen(false);
            }
            if (isAccountDropdownOpen) {
              setIsAccountDropdownOpen(false);
            }
          }}
        />
      )}
    </>
  );
};

export default Header;
